import { Shield, FileCheck, Download, CheckCircle, Lock, Zap, Scale, Moon, Sun, Search, FolderLock, QrCode, ShieldCheck, Trash2, BookOpen, Link2, Clock, Globe, Database, PenTool, UserCheck, Building2 } from 'lucide-react'
import { APP_VERSION } from '@/shared/lib/constants'
import { Link } from 'react-router-dom'
import { useTheme } from '@/shared/contexts/ThemeContext'
import { useCountry, type Country } from '@/shared/hooks/useCountry'

const CONTENT: Record<Country, {
  heroSubtitle: string
  heroBody: string
  law: string
  verifierCard: string
  firmDigitalTitle: string
  firmDigitalBody: string
  firmaElectronicaBody: string
  implicanciasBody: string
  step2Body: string
  pkcs12Body: string
  ctaBody: string
  footerTag: string
  footerLaw: string
}> = {
  AR: {
    heroSubtitle: 'Evidencia Digital para Procesos Judiciales en Argentina',
    heroBody: 'Verificá firmas digitales y electrónicas en documentos judiciales. Obtené constancias probatorias profesionales según la',
    law: 'Ley 25.506',
    verifierCard: 'Verificá firmas digitales y electrónicas en PDFs según Ley 25.506',
    firmDigitalTitle: 'Firma Digital (Ley 25.506)',
    firmDigitalBody: 'Validación de certificados PKI argentinos con plena validez probatoria en tribunales y expedientes judiciales.',
    firmaElectronicaBody: 'Detección de firmas sin certificado digital, con menor valor probatorio según jurisprudencia argentina.',
    implicanciasBody: 'Análisis automático del valor probatorio según normativa argentina vigente.',
    step2Body: 'Análisis técnico de certificados PKI, validez temporal y cadena de confianza según estándares argentinos.',
    pkcs12Body: 'Usa tu certificado .p12 o .pfx para firmar con plena validez legal según Ley 25.506',
    ctaBody: 'Obtené constancias probatorias profesionales para tus expedientes judiciales en Argentina.',
    footerTag: 'Evidencia Digital para Argentina',
    footerLaw: 'Herramienta técnica de verificación según Ley 25.506',
  },
  EC: {
    heroSubtitle: 'Evidencia Digital para Contratos y Documentos en Ecuador',
    heroBody: 'Verificá firmas digitales y electrónicas en PDFs. Constancias probatorias reconocidas según la',
    law: 'Ley 67 de Comercio Electrónico',
    verifierCard: 'Verificá firmas digitales y electrónicas en PDFs según Ley 67 (todas las CAs acreditadas por ARCOTEL)',
    firmDigitalTitle: 'Firma Digital (Ley 67 / ARCOTEL)',
    firmDigitalBody: 'Validación de certificados PKI ecuatorianos: BCE, Security Data, ANF, Lazzate y 12 CAs más acreditadas por ARCOTEL.',
    firmaElectronicaBody: 'Detección de firmas sin certificado PKI acreditado, con menor valor probatorio según Ley 67.',
    implicanciasBody: 'Análisis automático del valor probatorio según la Ley Orgánica de Comercio Electrónico y normativa ARCOTEL.',
    step2Body: 'Análisis técnico de certificados PKI, validez temporal y cadena de confianza según ARCOTEL-2024-0176.',
    pkcs12Body: 'Usa tu certificado .p12 emitido por BCE, Security Data, ANF u otra CA acreditada por ARCOTEL',
    ctaBody: 'Cobertura total de la PKI ecuatoriana: todas las CAs acreditadas por ARCOTEL disponibles.',
    footerTag: 'Evidencia Digital para Ecuador',
    footerLaw: 'Herramienta técnica de verificación según Ley 67 de Comercio Electrónico',
  },
}

interface LandingPageProps {
  onStartVerification: () => void
}

export function LandingPage({ onStartVerification }: LandingPageProps) {
  const { theme, toggleTheme } = useTheme()
  const { country, setCountry } = useCountry()
  const c = CONTENT[country]

  return (
    <div className="min-h-screen bg-gradient-to-br from-legal-50 via-white to-legal-50 dark:from-legal-900 dark:via-legal-800 dark:to-legal-900">
      {/* Controls - Fixed Top Right */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50 flex items-center gap-2">
        {/* Country toggle */}
        <div className="flex items-center bg-white dark:bg-legal-800 border border-legal-200 dark:border-legal-700 rounded-lg shadow-md overflow-hidden text-sm">
          <button
            onClick={() => setCountry('AR')}
            className={`px-3 py-2 transition-colors ${country === 'AR' ? 'bg-primary-600 text-white' : 'text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700'}`}
            aria-label="Argentina"
          >
            🇦🇷 <span className="hidden sm:inline">AR</span>
          </button>
          <button
            onClick={() => setCountry('EC')}
            className={`px-3 py-2 transition-colors ${country === 'EC' ? 'bg-primary-600 text-white' : 'text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700'}`}
            aria-label="Ecuador"
          >
            🇪🇨 <span className="hidden sm:inline">EC</span>
          </button>
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-white dark:bg-legal-800 text-legal-600 dark:text-legal-300 hover:text-legal-800 dark:hover:text-white border border-legal-200 dark:border-legal-700 rounded-lg transition-colors shadow-md"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          <span className="hidden sm:inline">{theme === 'dark' ? 'Claro' : 'Oscuro'}</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary-600 dark:bg-primary-500 text-white mb-6">
            <Shield size={40} />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
            <span className="text-legal-900 dark:text-white">Sign</span>
            <span className="text-primary-600 dark:text-primary-400">Wiz</span>
          </h1>

          <p className="text-2xl text-legal-700 dark:text-legal-200 mb-4 font-medium">
            {c.heroSubtitle}
          </p>

          <p className="text-lg text-gray-600 dark:text-gray-300 mb-10 max-w-2xl mx-auto">
            {c.heroBody}{' '}
            <span className="font-semibold text-legal-700 dark:text-legal-300">{c.law}</span>
            {country === 'AR' ? ' para expedientes y demandas.' : '.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={onStartVerification}
              className="btn-primary px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Verificar Firma Digital
            </button>

            <Link
              to="/custody"
              className="inline-flex items-center gap-2 btn-secondary px-8 py-4 text-lg font-semibold"
            >
              <FolderLock size={20} />
              Custodia Documental
            </Link>

            <Link
              to="/firmar"
              className="inline-flex items-center gap-2 btn-secondary px-8 py-4 text-lg font-semibold"
            >
              <PenTool size={20} />
              Firmar Documento
            </Link>

            <Link
              to="/qr-generator"
              className="inline-flex items-center gap-2 text-legal-600 dark:text-legal-300 hover:text-legal-800 dark:hover:text-white px-6 py-4 text-lg font-medium transition-colors"
            >
              <QrCode size={20} />
              Generar QR
            </Link>
          </div>
        </div>
      </div>

      {/* All Tools Section */}
      <div className="bg-white dark:bg-legal-800 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-legal-900 dark:text-white mb-4">
            Herramientas Disponibles
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
            Suite completa de herramientas para la gestión de evidencia digital en procesos judiciales
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {/* Verificador de Firmas */}
            <Link
              to="/verifier"
              className="group bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/50 dark:to-primary-800/50 p-6 rounded-xl border-2 border-transparent hover:border-primary-500 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-600 text-white mb-4 group-hover:scale-110 transition-transform">
                <FileCheck size={28} />
              </div>
              <h3 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                Verificador de Firmas
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {c.verifierCard}
              </p>
            </Link>

            {/* Custodia Documental */}
            <Link
              to="/custody"
              className="group bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50 p-6 rounded-xl border-2 border-transparent hover:border-green-500 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-600 text-white mb-4 group-hover:scale-110 transition-transform">
                <FolderLock size={28} />
              </div>
              <h3 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                Custodia Documental
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Genera constancias con QR para cartas documento y traslados
              </p>
            </Link>

            {/* Generador QR */}
            <Link
              to="/qr-generator"
              className="group bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/50 dark:to-purple-800/50 p-6 rounded-xl border-2 border-transparent hover:border-purple-500 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-600 text-white mb-4 group-hover:scale-110 transition-transform">
                <QrCode size={28} />
              </div>
              <h3 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                Generador QR
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Creá códigos QR para enlaces de expedientes y documentos
              </p>
            </Link>

            {/* Firma de Documentos */}
            <Link
              to="/firmar"
              className="group bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/50 dark:to-orange-800/50 p-6 rounded-xl border-2 border-transparent hover:border-orange-500 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-600 text-white mb-4 group-hover:scale-110 transition-transform">
                <PenTool size={28} />
              </div>
              <h3 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                Firma de Documentos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Firma digital con certificado PKCS#12 (.p12/.pfx), verificable por cualquier lector PDF
              </p>
            </Link>

            {/* Blockchain Lookup */}
            <Link
              to="/blockchain-lookup"
              className="group bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 p-6 rounded-xl border-2 border-transparent hover:border-blue-500 transition-all hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-600 text-white mb-4 group-hover:scale-110 transition-transform">
                <Search size={28} />
              </div>
              <h3 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                Blockchain Lookup
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Investiga direcciones en 10+ redes blockchain
              </p>
            </Link>
          </div>

          {/* Blog link */}
          <div className="text-center mt-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-legal-600 dark:text-legal-300 hover:text-legal-800 dark:hover:text-white transition-colors"
            >
              <BookOpen size={18} />
              <span>Ver guías y artículos en el blog</span>
            </Link>
          </div>
        </div>
      </div>

      {/* How Signature Verification Works */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-legal-900 dark:text-white mb-12">
            ¿Cómo funciona la Verificación de Firmas?
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 mb-4">
                <FileCheck size={32} />
              </div>
              <h3 className="text-xl font-semibold text-legal-900 dark:text-white mb-3">1. Subí tu PDF</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Arrastrá o seleccioná el documento PDF que querés verificar. Tu archivo se mantiene privado.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 mb-4">
                <Zap size={32} />
              </div>
              <h3 className="text-xl font-semibold text-legal-900 dark:text-white mb-3">2. Verificación Pericial</h3>
              <p className="text-gray-600 dark:text-gray-300">
                {c.step2Body}
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 mb-4">
                <Download size={32} />
              </div>
              <h3 className="text-xl font-semibold text-legal-900 dark:text-white mb-3">3. Constancia Probatoria</h3>
              <p className="text-gray-600 dark:text-gray-300">
                PDF profesional con resultado técnico, validez probatoria y referencias legales para adjuntar a expedientes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What We Verify Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-legal-900 dark:text-white mb-12">
            ¿Qué verificamos?
          </h2>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-legal-100 dark:border-legal-700">
              <div className="flex items-start gap-4">
                <CheckCircle className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-legal-900 dark:text-white">{c.firmDigitalTitle}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {c.firmDigitalBody}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-legal-100 dark:border-legal-700">
              <div className="flex items-start gap-4">
                <CheckCircle className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-legal-900 dark:text-white">Firma Electrónica</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {c.firmaElectronicaBody}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-legal-100 dark:border-legal-700">
              <div className="flex items-start gap-4">
                <Lock className="text-legal-600 dark:text-legal-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-legal-900 dark:text-white">Integridad del Documento</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Hash SHA-256 calculado localmente para verificar que el documento no fue modificado.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-legal-100 dark:border-legal-700">
              <div className="flex items-start gap-4">
                <Scale className="text-legal-600 dark:text-legal-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-legal-900 dark:text-white">Implicancias Legales</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {c.implicanciasBody}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Jurisdicciones soportadas */}
      <div className="bg-white dark:bg-legal-800 py-10 border-t border-legal-100 dark:border-legal-700">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl font-bold text-legal-900 dark:text-white mb-2">
              Jurisdicciones soportadas
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Validacion multi-pais. Usa el selector en la esquina superior derecha para ver el contenido de cada pais.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              <div className={`p-4 rounded-xl border-2 ${country === 'AR' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-legal-200 dark:border-legal-700'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🇦🇷</span>
                  <span className="font-bold text-legal-900 dark:text-white">Argentina</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Ley 25.506 de Firma Digital</p>
                <div className="flex flex-wrap gap-1">
                  {['AFIP', 'AC Ministerios', 'IRAM', 'PKI-AR', 'CIDI Córdoba'].map(ca => (
                    <span key={ca} className="px-2 py-0.5 bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300 text-xs rounded">
                      {ca}
                    </span>
                  ))}
                </div>
              </div>
              <div className={`p-4 rounded-xl border-2 ${country === 'EC' ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-legal-200 dark:border-legal-700'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🇪🇨</span>
                  <span className="font-bold text-legal-900 dark:text-white">Ecuador</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Ley 67 — todas las CAs acreditadas por ARCOTEL</p>
                <div className="flex flex-wrap gap-1">
                  {['BCE', 'Security Data', 'ANF', 'Lazzate', 'Datil', 'AppFirmas', '+10 más'].map(ca => (
                    <span key={ca} className="px-2 py-0.5 bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300 text-xs rounded">
                      {ca}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Custody Section - Privacy First */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-legal-800 dark:to-legal-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600 dark:bg-green-500 text-white mb-4">
                <FolderLock size={32} />
              </div>
              <h2 className="text-3xl font-bold text-legal-900 dark:text-white mb-4">
                Custodia Documental con QR
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-200 max-w-3xl mx-auto">
                Generá códigos QR de verificación para tus documentos legales. Ideal para{' '}
                <span className="font-semibold">cartas documento, contratos, poderes y traslados de demanda</span>.
              </p>
            </div>

            {/* Privacy Highlight */}
            <div className="bg-white dark:bg-legal-800 p-8 rounded-xl shadow-lg border-2 border-green-500 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <ShieldCheck className="text-green-600 dark:text-green-400" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-legal-900 dark:text-white">
                  Privacidad Total
                </h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Lock className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Tu documento NUNCA se sube</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Solo se calcula el hash localmente en tu navegador
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Download className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Descarga inmediata</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      PDF y QR disponibles al instante sin necesidad de cuenta
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Trash2 className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-legal-900 dark:text-white">Nada queda guardado</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Al cerrar la página se borra todo automáticamente
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <h3 className="font-semibold text-xl mb-3 text-legal-900 dark:text-white flex items-center gap-2">
                  <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
                  Casos de Uso
                </h3>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Traslado de demanda vía carta documento
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Intimaciones y reclamos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Contratos y acuerdos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Documentación con valor probatorio
                  </li>
                </ul>
              </div>

              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <h3 className="font-semibold text-xl mb-3 text-legal-900 dark:text-white flex items-center gap-2">
                  <QrCode className="text-green-600 dark:text-green-400" size={24} />
                  Flujo Rápido
                </h3>
                <ol className="space-y-2 text-gray-600 dark:text-gray-300 list-decimal list-inside">
                  <li>Seleccioná tu documento PDF</li>
                  <li>Completá los datos opcionales</li>
                  <li>Generá la constancia con QR</li>
                  <li>Insertá el QR en tu documento original</li>
                  <li>Descargá todo y listo</li>
                </ol>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/custody"
                className="inline-flex items-center gap-2 btn-primary px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-green-600 hover:bg-green-700"
              >
                <FolderLock size={20} />
                Iniciar Custodia Documental
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Blockchain Lookup Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-legal-800 dark:to-legal-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 dark:bg-blue-500 text-white mb-4">
                <Search size={32} />
              </div>
              <h2 className="text-3xl font-bold text-legal-900 dark:text-white mb-4">
                Investigación Blockchain
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-200 max-w-3xl mx-auto">
                Consultá información de cualquier dirección blockchain en segundos. Sin necesidad de saber qué explorador usar.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <h3 className="font-semibold text-xl mb-3 text-legal-900 dark:text-white flex items-center gap-2">
                  <CheckCircle className="text-blue-600 dark:text-blue-400" size={24} />
                  10+ Redes Soportadas
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Ethereum, Bitcoin, BSC, Polygon, Arbitrum, Optimism, Base, Avalanche, Fantom y Solana.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {['ETH', 'BTC', 'BSC', 'MATIC', 'ARB', 'OP', 'AVAX', 'SOL'].map((token) => (
                    <span key={token} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded">
                      {token}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <h3 className="font-semibold text-xl mb-3 text-legal-900 dark:text-white flex items-center gap-2">
                  <Zap className="text-blue-600 dark:text-blue-400" size={24} />
                  Auto-Detección Inteligente
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Pegá cualquier dirección (0x..., bc1..., 1...) y el sistema detecta automáticamente la red correcta.
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                    Balance nativo actual
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                    Primera y última actividad
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                    Link directo al explorador oficial
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white dark:bg-legal-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-legal-700">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-legal-900 dark:text-white mb-2">
                    Casos de Uso Legal
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Ideal para investigación de fraude, rastreo de activos en divorcios, embargos y peritajes de criptomonedas.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-3 py-1 bg-legal-100 dark:bg-legal-700 text-legal-700 dark:text-legal-300 rounded-full">
                      Fraude investigación
                    </span>
                    <span className="px-3 py-1 bg-legal-100 dark:bg-legal-700 text-legal-700 dark:text-legal-300 rounded-full">
                      Asset tracking
                    </span>
                    <span className="px-3 py-1 bg-legal-100 dark:bg-legal-700 text-legal-700 dark:text-legal-300 rounded-full">
                      Evidencia digital
                    </span>
                    <span className="px-3 py-1 bg-legal-100 dark:bg-legal-700 text-legal-700 dark:text-legal-300 rounded-full">
                      Peritajes crypto
                    </span>
                  </div>
                </div>
                <div>
                  <Link
                    to="/blockchain-lookup"
                    className="inline-flex items-center gap-2 btn-primary px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
                  >
                    <Search size={20} />
                    Buscar Dirección
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Firma de Documentos Section */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-legal-800 dark:to-legal-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-600 dark:bg-orange-500 text-white mb-4">
                <PenTool size={32} />
              </div>
              <h2 className="text-3xl font-bold text-legal-900 dark:text-white mb-4">
                Firma de Documentos
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-200 max-w-3xl mx-auto">
                Firma digitalmente tus PDFs con certificado PKCS#12 (.p12/.pfx).
                El documento firmado es verificable por cualquier lector PDF compatible.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mb-3">
                  <ShieldCheck className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Certificado PKCS#12</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {c.pkcs12Body}
                </p>
              </div>

              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mb-3">
                  <FileCheck className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Verificable universalmente</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  El PDF firmado puede ser verificado por Adobe Reader, Foxit y cualquier lector PDF compatible
                </p>
              </div>

              <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-legal-700">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mb-3">
                  <Link2 className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">ID de verificación</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Cada firma genera un ID de verificación publica que permite confirmar la autenticidad del documento
                </p>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/firmar"
                className="inline-flex items-center gap-2 btn-primary px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all bg-orange-600 hover:bg-orange-700"
              >
                <PenTool size={20} />
                Firmar Documento
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Coming Soon: Blockchain Registration */}
      <div className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-legal-800 dark:to-legal-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1 bg-amber-200 dark:bg-amber-900 text-amber-800 dark:text-amber-200 text-sm font-semibold rounded-full mb-4">
                Próximamente
              </span>
              <h2 className="text-3xl font-bold text-legal-900 dark:text-white mb-4">
                Registro en Blockchain
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-200 max-w-3xl mx-auto">
                Inmutabilidad y prueba de existencia descentralizada para tus documentos legales.
                El siguiente nivel en evidencia digital probatoria.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <div className="bg-white dark:bg-legal-800 p-5 rounded-xl shadow-md border border-amber-200 dark:border-amber-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-3">
                  <Database className="text-amber-600 dark:text-amber-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Inmutabilidad</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Una vez registrado en blockchain, el hash del documento no puede ser alterado ni eliminado
                </p>
              </div>

              <div className="bg-white dark:bg-legal-800 p-5 rounded-xl shadow-md border border-amber-200 dark:border-amber-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-3">
                  <Clock className="text-amber-600 dark:text-amber-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Timestamp Descentralizado</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Prueba de existencia en fecha exacta sin depender de una autoridad central
                </p>
              </div>

              <div className="bg-white dark:bg-legal-800 p-5 rounded-xl shadow-md border border-amber-200 dark:border-amber-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-3">
                  <Globe className="text-amber-600 dark:text-amber-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Verificación Pública</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Cualquier persona puede verificar el registro en exploradores blockchain públicos
                </p>
              </div>

              <div className="bg-white dark:bg-legal-800 p-5 rounded-xl shadow-md border border-amber-200 dark:border-amber-900">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mb-3">
                  <Link2 className="text-amber-600 dark:text-amber-400" size={24} />
                </div>
                <h3 className="font-bold text-legal-900 dark:text-white mb-2">Sin Punto de Fallo</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  La prueba persiste incluso si SignWiz deja de existir. Independencia total
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-legal-800 p-6 rounded-xl shadow-lg border-2 border-amber-300 dark:border-amber-700">
              <h3 className="font-bold text-xl text-legal-900 dark:text-white mb-4 flex items-center gap-2">
                <Scale className="text-amber-600" size={24} />
                ¿Por qué Blockchain para Evidencia Legal?
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Jurisprudencia creciente:</strong> Tribunales argentinos e internacionales aceptan evidencia blockchain</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Prueba anticipada:</strong> Ideal para conflictos donde la fecha de creación es clave</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Propiedad intelectual:</strong> Registro de autoría y fecha de creación de obras</span>
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Contratos privados:</strong> Prueba de existencia del acuerdo en fecha determinada</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Due diligence:</strong> Documentación de procesos de auditoría y compliance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Notificaciones fehacientes:</strong> Complemento probatorio para cartas documento</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Acceso Seguro */}
      <div className="py-12 bg-white dark:bg-legal-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-legal-50 to-primary-50 dark:from-legal-700 dark:to-legal-800 p-8 rounded-xl border border-legal-200 dark:border-legal-600">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                  <UserCheck className="text-primary-600 dark:text-primary-400" size={32} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-legal-900 dark:text-white mb-2">
                    Acceso Seguro con Cuenta Personal
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Crea tu cuenta en SignWiz para guardar historial de verificaciones, firmas y constancias.
                    Accede desde cualquier dispositivo con tus credenciales.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="btn-primary px-6 py-3 font-semibold whitespace-nowrap"
                >
                  Iniciar Sesión
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SmartWiz Integration */}
      <div className="py-12 bg-gradient-to-r from-legal-50 to-primary-50 dark:from-legal-900 dark:to-legal-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-legal-800 p-8 rounded-xl shadow-lg border border-legal-200 dark:border-legal-700">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-legal-100 dark:bg-legal-700 flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-legal-600 dark:text-legal-400" size={32} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-legal-900 dark:text-white mb-2">
                    Integración con SmartWiz
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Las organizaciones que usan{' '}
                    <a
                      href="https://smartwiz.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
                    >
                      SmartWiz
                    </a>
                    {' '}pueden firmar y verificar documentos directamente desde su panel de administracion.
                    Gestion documental empresarial integrada con firma digital.
                  </p>
                </div>
                <a
                  href="https://smartwiz.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary px-6 py-3 font-semibold whitespace-nowrap"
                >
                  Conocer SmartWiz
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ecosystem Section */}
      <div className="bg-legal-900 dark:bg-legal-950 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Proximas Herramientas</h2>
            <p className="text-legal-200 mb-8">
              Mas herramientas para la gestion completa de evidencia digital en procesos judiciales.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
              {[
                'Validador de capturas de WhatsApp',
                'Captura certificada de páginas web',
                'Analisis de metadatos EXIF',
                'Solicitudes ARCO automatizadas',
                'Gestion de evidencia digital',
                'Notificaciones automáticas',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2 bg-legal-800 p-3 rounded-lg">
                  <CheckCircle size={18} className="text-legal-400 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-legal-600 to-legal-700 dark:from-legal-700 dark:to-legal-800 rounded-2xl p-12 text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-4 text-white">¿Listo para verificar tu documento?</h2>
            <p className="text-xl text-white mb-8">
              {c.ctaBody}
            </p>
            <button
              onClick={onStartVerification}
              className="btn-primary px-10 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Verificar Ahora
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-legal-50 dark:bg-legal-900 py-8 border-t border-legal-100 dark:border-legal-700">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <div className="text-gray-600 dark:text-gray-300 text-center md:text-left">
              <p className="text-sm">
                © 2025 <span className="text-legal-900 dark:text-white">Sign</span><span className="text-primary-600 dark:text-primary-400">Wiz</span> v{APP_VERSION} • {c.footerTag}
              </p>
              <p className="text-xs mt-1">
                {c.footerLaw}
              </p>
              <p className="text-xs mt-2">
                Desarrollado por{' '}
                <a
                  href="https://arteclaw.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 font-medium transition-colors"
                >
                  Arteclaw
                </a>
              </p>
            </div>

            <div className="flex flex-wrap gap-4 justify-center md:justify-end">
              <Link to="/blog" className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 transition-colors">
                Blog
              </Link>
              <button
                onClick={onStartVerification}
                className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 transition-colors"
              >
                Verificar
              </button>
              <Link to="/terminos" className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 transition-colors">
                Términos
              </Link>
              <Link to="/privacidad" className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-300 transition-colors">
                Privacidad
              </Link>
            </div>
          </div>

          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            No constituye asesoramiento legal. Consultá con profesionales del derecho para tu caso específico.
          </p>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-3">
            Desarrollado por <a href="https://arteclaw.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Arteclaw</a>
          </p>
        </div>
      </footer>
    </div>
  )
}
